import { NOTE_MAX } from "./leads";

export type FollowUpView = "mine" | "all";
export type FollowUpDue = "now" | "open";
export const MISSING = ["phone", "qualifier", "owner"] as const;
export type Missing = (typeof MISSING)[number];

export const MISSING_TEXT: Record<Missing, string> = {
  phone: "No phone",
  qualifier: "No qualifier",
  owner: "No owner",
};

const DAY = 86_400_000;

const days = (from: string, to: string) => Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / DAY);

/** "Today", "Today 14:30", "3 days late", "Tomorrow" or "In 5 days". */
export function dueText(dueOn: string, dueAt: string | null, today: string): string {
  const gap = days(today, dueOn);
  const time = dueAt ? ` ${dueAt}` : "";
  if (gap === 0) return `Today${time}`;
  if (gap === 1) return `Tomorrow${time}`;
  if (gap === -1) return "1 day late";
  return gap < 0 ? `${-gap} days late` : `In ${gap} days`;
}

export const isLate = (dueOn: string, today: string) => dueOn < today;

/** The follow-ups grouped by owner. The inbox (no owner) comes last. */
export function byOwner<T extends { owner_email: string | null }>(items: T[]): [string | null, T[]][] {
  const groups = new Map<string | null, T[]>();
  for (const item of items) {
    const list = groups.get(item.owner_email) ?? [];
    list.push(item);
    groups.set(item.owner_email, list);
  }
  return [...groups.entries()].sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : a.localeCompare(b)));
}

/** The problem of a new follow-up: no day, a day before today, no text, or too much text. */
export function followUpError(dueOn: string, note: string, today: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dueOn)) return "Choose the day of the follow-up.";
  if (dueOn < today) return "Choose today or a later day.";
  if (!note.trim()) return "Write what to do.";
  if (note.trim().length > NOTE_MAX) return `The note can have ${NOTE_MAX} characters at most.`;
  return "";
}
